import React, { useState } from "react";
import {
  FaBed,
  FaCouch,
  FaUtensils,
  FaSwimmer,
  FaBath,
  FaToilet,
  FaTree,
  FaHome,
  FaGamepad,
  FaUmbrellaBeach,
  FaWifi,
  FaConciergeBell,
  FaDumbbell,
  FaSpa,
  FaMountain,
  FaWineGlassAlt,
  FaFire,
  FaCar,
  FaBook,
} from "react-icons/fa";
import { useData } from "../../../context/DataContext";
import ImageUploadField from "../../../utils/ImageUploadField";
import { confirmDelete, successToast } from "../../../utils/dialog";

// Curated icon catalog. Keep names in sync with frontend PhotoGallery iconMap.
const ICON_OPTIONS = [
  { name: "FaHome", label: "Home", Icon: FaHome },
  { name: "FaBed", label: "Bedroom", Icon: FaBed },
  { name: "FaCouch", label: "Living", Icon: FaCouch },
  { name: "FaUtensils", label: "Dining", Icon: FaUtensils },
  { name: "FaGamepad", label: "Indoor Games", Icon: FaGamepad },
  { name: "FaSwimmer", label: "Swim", Icon: FaSwimmer },
  { name: "FaBath", label: "Jacuzzi/Pool", Icon: FaBath },
  { name: "FaUmbrellaBeach", label: "Outdoors", Icon: FaUmbrellaBeach },
  { name: "FaTree", label: "Garden", Icon: FaTree },
  { name: "FaMountain", label: "View", Icon: FaMountain },
  { name: "FaToilet", label: "Washroom", Icon: FaToilet },
  { name: "FaSpa", label: "Spa", Icon: FaSpa },
  { name: "FaDumbbell", label: "Gym", Icon: FaDumbbell },
  { name: "FaWifi", label: "Wifi", Icon: FaWifi },
  { name: "FaConciergeBell", label: "Service", Icon: FaConciergeBell },
  { name: "FaWineGlassAlt", label: "Bar", Icon: FaWineGlassAlt },
  { name: "FaFire", label: "Bonfire", Icon: FaFire },
  { name: "FaCar", label: "Parking", Icon: FaCar },
  { name: "FaBook", label: "Library", Icon: FaBook },
];

const IconPicker = ({ value, onChange }) => (
  <div className="grid grid-cols-6 sm:grid-cols-8 md:grid-cols-10 gap-2">
    <button
      type="button"
      onClick={() => onChange(null)}
      title="No icon"
      className={`flex flex-col items-center justify-center gap-1 p-2 rounded-lg border text-xs transition ${
        !value
          ? "border-blue-500 bg-blue-50 text-blue-700"
          : "border-gray-200 bg-white text-gray-500 hover:border-gray-300"
      }`}
    >
      <span className="text-lg leading-none">—</span>
      <span>None</span>
    </button>
    {ICON_OPTIONS.map(({ name, label, Icon }) => {
      const active = value === name;
      return (
        <button
          key={name}
          type="button"
          onClick={() => onChange(name)}
          title={label}
          className={`flex flex-col items-center justify-center gap-1 p-2 rounded-lg border text-xs transition ${
            active
              ? "border-blue-500 bg-blue-50 text-blue-700"
              : "border-gray-200 bg-white text-gray-500 hover:border-gray-300"
          }`}
        >
          <Icon size={18} />
          <span className="truncate w-full text-center">{label}</span>
        </button>
      );
    })}
  </div>
);

const renderIcon = (iconName, size = 18) => {
  const found = ICON_OPTIONS.find((o) => o.name === iconName);
  if (!found) return null;
  const Icon = found.Icon;
  return <Icon size={size} />;
};

const GalleryManager = () => {
  const {
    galleryData,
    addGallerySection,
    addImageToSection,
    deleteImageFromSection,
    deleteGallerySection,
    updateGallerySectionName,
    updateGallerySectionIcon,
    updateImageLabel,
  } = useData();

  const [newSectionName, setNewSectionName] = useState("");
  const [newSectionIcon, setNewSectionIcon] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [imageLabel, setImageLabel] = useState("");
  const [selectedSectionId, setSelectedSectionId] = useState("");

  const [editingSectionId, setEditingSectionId] = useState(null);
  const [editingSectionName, setEditingSectionName] = useState("");
  const [iconEditingSectionId, setIconEditingSectionId] = useState(null);

  const [editingImageId, setEditingImageId] = useState(null);
  const [newLabel, setNewLabel] = useState("");

  const handleAddSection = async () => {
    if (newSectionName.trim() !== "") {
      await addGallerySection(newSectionName, newSectionIcon);
      setNewSectionName("");
      setNewSectionIcon(null);
    }
  };

  const handleAddImage = async () => {
    if (selectedSectionId && selectedFile && imageLabel.trim() !== "") {
      await addImageToSection(selectedSectionId, selectedFile, imageLabel);
      setSelectedFile(null);
      setImageLabel("");
    }
  };

  const handleEditSectionName = async (sectionId) => {
    if (editingSectionName.trim() !== "") {
      await updateGallerySectionName(sectionId, editingSectionName);
      setEditingSectionId(null);
      setEditingSectionName("");
    }
  };

  const handleUpdateIcon = async (sectionId, icon) => {
    await updateGallerySectionIcon(sectionId, icon);
  };

  const startEditingLabel = (imgId, currentLabel) => {
    setEditingImageId(imgId);
    setNewLabel(currentLabel);
  };

  const handleUpdateLabel = async (sectionId, imgId) => {
    if (newLabel.trim() !== "") {
      await updateImageLabel(sectionId, imgId, newLabel);
      setEditingImageId(null);
      setNewLabel("");
    }
  };

  const handleDeleteSection = async (sectionId) => {
    if (
      await confirmDelete({
        title: "Delete Section?",
        text: "This section and all of its images will be permanently deleted.",
      })
    ) {
      await deleteGallerySection(sectionId);
      successToast("Section deleted successfully.");
    }
  };

  const handleDeleteImage = async (sectionId, imgId) => {
    if (
      await confirmDelete({
        title: "Delete Image?",
        text: "This image will be permanently deleted from the gallery.",
      })
    ) {
      await deleteImageFromSection(sectionId, imgId);
      successToast("Image deleted successfully.");
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-10">
      <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">
        Gallery Manager
      </h2>

      {/* Add Section */}
      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 space-y-4">
        <h4 className="text-lg font-semibold text-gray-700 flex items-center gap-2">
          <span className="text-blue-500 text-xl">+</span>
          Add New Section
        </h4>
        <div className="flex flex-col sm:flex-row gap-4 items-end">
          <div className="w-full sm:flex-1">
            <label
              htmlFor="sectionName"
              className="block text-sm font-semibold text-gray-600 mb-1"
            >
              Section Name <span className="text-red-500">*</span>
            </label>
            <input
              id="sectionName"
              type="text"
              value={newSectionName}
              onChange={(e) => setNewSectionName(e.target.value)}
              placeholder="Enter section name"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
              required
            />
          </div>
          <button
            onClick={handleAddSection}
            className="w-full sm:w-auto px-6 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={!newSectionName.trim()}
          >
            Add Section
          </button>
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-600 mb-2">
            Section Icon (optional)
          </label>
          <IconPicker value={newSectionIcon} onChange={setNewSectionIcon} />
        </div>
      </div>

      {/* Upload Image */}
      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 space-y-4">
        <h4 className="text-lg font-semibold text-gray-700 flex items-center gap-2">
          <span className="text-green-500 text-xl">&#8593;</span>
          Add Image to Section
        </h4>
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="sectionSelect"
                className="block text-sm font-semibold text-gray-600 mb-1"
              >
                Section <span className="text-red-500">*</span>
              </label>
              <select
                id="sectionSelect"
                value={selectedSectionId}
                onChange={(e) => setSelectedSectionId(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#D48C1E] focus:border-transparent"
                required
              >
                <option value="">Select section</option>
                {galleryData.map((section) => (
                  <option key={section.id} value={section.id}>
                    {section.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label
                htmlFor="imageLabel"
                className="block text-sm font-semibold text-gray-600 mb-1"
              >
                Image Label
              </label>
              <input
                id="imageLabel"
                type="text"
                value={imageLabel}
                onChange={(e) => setImageLabel(e.target.value)}
                placeholder="Enter image label"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D48C1E] focus:border-transparent"
              />
            </div>
          </div>
          <ImageUploadField
            id="imageFile"
            label="Image File"
            hint="Recommended size: 800×600px"
            file={selectedFile}
            required
            onChange={(e) => setSelectedFile(e.target.files[0])}
            onClear={() => setSelectedFile(null)}
          />
        </div>
        <div className="flex justify-center pt-2">
          <button
            onClick={handleAddImage}
            className="px-8 py-3 bg-[#14251F] text-[#FFD1A0] font-medium rounded-lg hover:bg-[#1f3a30] transition disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={!selectedSectionId || !selectedFile}
          >
            Upload Image
          </button>
        </div>
      </div>

      {/* Display Gallery Sections */}
      <div className="space-y-8">
        {galleryData.map((section) => (
          <div
            key={section.id}
            className="bg-white p-6 rounded-2xl shadow-md border border-gray-200 space-y-4"
          >
            <div className="flex flex-wrap justify-between items-center gap-2">
              {editingSectionId === section.id ? (
                <div className="flex items-center gap-2 flex-1">
                  <input
                    type="text"
                    value={editingSectionName}
                    onChange={(e) => setEditingSectionName(e.target.value)}
                    className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                  />
                  <button
                    onClick={() => handleEditSectionName(section.id)}
                    className="px-4 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition"
                  >
                    Save
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <span className="text-gray-700">
                    {section.icon ? renderIcon(section.icon, 22) : (
                      <span className="text-gray-300 text-sm italic">no icon</span>
                    )}
                  </span>
                  <h4 className="text-2xl font-semibold text-gray-800">
                    {section.name}
                  </h4>
                  <button
                    onClick={() => {
                      setEditingSectionId(section.id);
                      setEditingSectionName(section.name);
                    }}
                    className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() =>
                      setIconEditingSectionId(
                        iconEditingSectionId === section.id ? null : section.id
                      )
                    }
                    className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
                  >
                    {iconEditingSectionId === section.id ? "Close Icon" : "Change Icon"}
                  </button>
                </div>
              )}
              <button
                onClick={() => handleDeleteSection(section.id)}
                className="px-4 py-1.5 bg-red-500 text-white text-sm rounded-lg hover:bg-red-600 transition"
              >
                Delete Section
              </button>
            </div>

            {iconEditingSectionId === section.id && (
              <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                <p className="text-sm font-semibold text-gray-600 mb-2">
                  Pick an icon for "{section.name}"
                </p>
                <IconPicker
                  value={section.icon || null}
                  onChange={(icon) => handleUpdateIcon(section.id, icon)}
                />
              </div>
            )}

            {section.images && section.images.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {section.images.map((img) => (
                  <div
                    key={img.id}
                    className="relative border border-gray-200 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition group"
                  >
                    <img
                      src={img.url}
                      alt={img.label}
                      className="w-full h-40 object-cover"
                    />
                    <div className="p-3 bg-gray-50 flex items-center gap-2">
                      {editingImageId === img.id ? (
                        <>
                          <input
                            type="text"
                            value={newLabel}
                            onChange={(e) => setNewLabel(e.target.value)}
                            className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                          />
                          <button
                            onClick={() => handleUpdateLabel(section.id, img.id)}
                            className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 transition"
                          >
                            Save
                          </button>
                        </>
                      ) : (
                        <>
                          <span className="text-sm text-gray-700 truncate flex-1">
                            {img.label}
                          </span>
                          <button
                            onClick={() =>
                              startEditingLabel(img.id, img.label)
                            }
                            className="text-xs text-blue-600 hover:text-blue-800 hover:underline shrink-0"
                          >
                            Edit
                          </button>
                        </>
                      )}
                    </div>
                    <button
                      onClick={() =>
                        handleDeleteImage(section.id, img.id)
                      }
                      className="absolute top-2 right-2 bg-red-500 text-white text-xs w-6 h-6 rounded-full hover:bg-red-600 transition opacity-0 group-hover:opacity-100 flex items-center justify-center"
                    >
                      &times;
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-400 text-sm text-center py-8">
                No images in this section yet.
              </p>
            )}
          </div>
        ))}
        {galleryData.length === 0 && (
          <p className="text-gray-400 text-center py-12">
            No gallery sections yet. Add one above.
          </p>
        )}
      </div>
    </div>
  );
};

export default GalleryManager;
